/**
 * combat/system.ts — When the World Bites Back
 *
 * Not every zone is cozy. Beyond the lamplit streets and flower gardens,
 * creatures stalk the deep forests, crystal caverns, and storm-lashed shores.
 * This system handles hostile encounters: aggro detection, auto-attack loops,
 * damage calculation, XP rewards, and the merciful respawn when things go wrong.
 *
 * Combat is opt-out — hostile creatures will engage you if you wander into
 * their territory. Cats, dogs, and tamed companions never attack. Dragons,
 * griffins, and wolves in danger zones will.
 */

import type { GameState, CombatStats, DamageNumber, DamageType, EquippedGear, WeaponVisual, ShieldVisual } from "../engine/state.js";
import type { CreatureState } from "../creature/state.js";
import { cleanupCreature } from "../creature/update.js";
// cleanupCreatureAttention is deferred to death animation expiry (loop.ts)
// to avoid re-creation during the 1.2s death render phase
import { addToast } from "../render/toasts.js";
import { addCombatLogEntry } from "../render/nameplate_ui.js";
import { duelDamageClamp, killPlayer } from "./pvp.js";
import { getBuffModifiers, absorbDamage } from "./buffs.js";
import {
  type MomentumState, type CombatFamiliarity, type AttunementData,
  type StyleTracker, type CombatAwarenessSnapshot,
  createMomentum, updateMomentum, applyHitImpulse, applyDamageTakenImpulse,
  getMomentumDamageMultiplier, getMomentumCritBonus, getMomentumSpeedMultiplier,
  resetMomentum,
  createFamiliarity, getNoveltyMultiplier, recordKill,
  createAttunement, recordWeaponHit, getAttunementDamageBonus,
  createStyleTracker, recordStyleData, buildCombatSnapshot,
} from "./dynamics.js";

// ─── Audio Engine Reference ───────────────────────────────────────────────
// Set from the game loop so combat can trigger sound effects without
// importing the audio module (avoids circular deps).

let audioEngine: any = null;

/** Wire the audio engine into the combat system. Call once from game loop init. */
export function setCombatAudioEngine(engine: any): void {
  audioEngine = engine;
}

/** Impact events returned by updateCombat for the renderer to emit particles. */
export interface ImpactEvent {
  /** World tile position where the hit landed */
  wx: number;
  wy: number;
  wz: number;
  isCrit: boolean;
}

// ─── Weapon Visual Mapping ──────────────────────────────────────────────────
// Maps loot weapon names to their visual representation on the character.

const WEAPON_VISUALS: Record<string, WeaponVisual> = {
  "Whittled Staff":         { class: "staff",    bladeColor: "#A0E0FF", handleColor: "#8B6B3E", accentColor: "#C8A060", length: 22 },
  "Forged Iron Blade":      { class: "sword_1h", bladeColor: "#B8C0C8", handleColor: "#5C3A1E", accentColor: "#A0882C", length: 18 },
  "Stormfang Blade":        { class: "sword_1h", bladeColor: "#88BBEE", handleColor: "#2A1A0E", accentColor: "#6688CC", length: 20 },
  "Griffin Talon Gauntlet":  { class: "dagger",   bladeColor: "#333340", handleColor: "#C0C0C0", accentColor: "#808090", length: 12 },
  // Axe variants
  "Woodsman's Hatchet":     { class: "axe",      bladeColor: "#A8A8A0", handleColor: "#6B4E2E", accentColor: "#8B7355", length: 14 },
  "Stonecleaver Axe":       { class: "axe",      bladeColor: "#C0C8D0", handleColor: "#4A3520", accentColor: "#B0A080", length: 18 },
  // Two-handed sword variants
  "Pilgrim's Greatsword":   { class: "sword_2h", bladeColor: "#C8D0D8", handleColor: "#5C3A20", accentColor: "#A09060", length: 26 },
  "Abyssal Claymore":       { class: "sword_2h", bladeColor: "#6070A0", handleColor: "#1A1020", accentColor: "#8060C0", length: 28 },
  // Mace variants
  "Ironbound Mace":         { class: "mace",     bladeColor: "#909090", handleColor: "#5C3A20", accentColor: "#706040", length: 14 },
  "Cinderheart Maul":       { class: "mace",     bladeColor: "#D0603A", handleColor: "#2A1810", accentColor: "#FFB040", length: 16 },
};

const SHIELD_VISUALS: Record<string, ShieldVisual> = {
  "Driftwood Shield":       { class: "buckler", faceColor: "#8B7355", rimColor: "#5C4A32", emblemColor: "#6B5B3E" },
  "Petrified Bark Shield":  { class: "kite",    faceColor: "#6B6B60", rimColor: "#4A4A3C", emblemColor: "#888878" },
  "Crystal Aegis":          { class: "tower",   faceColor: "#D0E8FF", rimColor: "#88AACC", emblemColor: "#FFFFFF" },
};

/** Get the visual for an equipped weapon, or null if none/fists */
export function getWeaponVisual(state: GameState): WeaponVisual | null {
  const wpn = state.player.equipment.weapon;
  if (!wpn) return null;
  return WEAPON_VISUALS[wpn.name] ?? null;
}

/** Get the visual for an equipped shield, or null */
export function getShieldVisual(state: GameState): ShieldVisual | null {
  const shd = state.player.equipment.shield;
  if (!shd) return null;
  return SHIELD_VISUALS[shd.name] ?? null;
}

/** Strike frame delay — damage fires 0.1s after attack starts (frame 1 at 10fps) */
const STRIKE_FRAME_DELAY = 0.1;

// ─── Creature HP Tracking ──────────────────────────────────────────────────
// CreatureState doesn't carry HP — we track it externally during combat.

const creatureCombatHp = new Map<string, { hp: number; maxHp: number }>();

// ─── Living Combat Systems ────────────────────────────────────────────────
// These dynamical systems replace flat arithmetic with continuous flows.
// Momentum is an ODE. Familiarity follows exponential decay. Attunement
// mirrors MeMo memory strengthening. The combat system is alive.

const momentum: MomentumState = createMomentum();
const familiarity: CombatFamiliarity = createFamiliarity();
const attunement: AttunementData = createAttunement();
const styleTracker: StyleTracker = createStyleTracker();

/** Most recent combat outcome for Aeterna awareness */
let recentOutcome: CombatAwarenessSnapshot["recentOutcome"] = null;
let recentOutcomeAt = 0;

/** Expose momentum for HUD rendering (flow state indicator, momentum bar) */
export function getMomentumState(): Readonly<MomentumState> { return momentum; }

/** Expose familiarity for stat display */
export function getFamiliarity(): Readonly<CombatFamiliarity> { return familiarity; }

/** Expose attunement for equipment UI */
export function getAttunementData(): Readonly<AttunementData> { return attunement; }

// ─── Combat XP Event Queue ────────────────────────────────────────────────
// A lightweight event channel so the combat UI can render the novelty
// multiplier that was applied to an XP gain. Combat system pushes an
// event when awarding XP on creature defeat; combat_ui.ts drains it
// when its passive XP observer detects the change to player.combat.xp.
// Multiple events can land in the same frame (AoE kills).

export interface CombatXpEvent {
  value: number;
  noveltyMult: number;
}
const _combatXpQueue: CombatXpEvent[] = [];

/** Drain the next pending combat XP event, or null if none. */
export function drainCombatXpEvent(): CombatXpEvent | null {
  return _combatXpQueue.shift() ?? null;
}

/** Peek at the next pending combat XP event without consuming it. */
export function peekCombatXpEvent(): CombatXpEvent | null {
  return _combatXpQueue[0] ?? null;
}

/**
 * Build combat awareness snapshot for the Aeterna neural pipeline.
 * Called from the Aeterna bridge to feed combat state into consciousness.
 */
export function getCombatAwareness(state: GameState): CombatAwarenessSnapshot {
  const cs = state.player.combatState;
  const hpRatio = state.player.combat.maxHp > 0
    ? state.player.combat.hp / state.player.combat.maxHp
    : 1;
  const dangerLevel = getCurrentDangerLevel(state);
  const weaponName = state.player.equipment.weapon?.name;
  return buildCombatSnapshot(
    momentum, familiarity, attunement, styleTracker,
    cs.active, hpRatio, dangerLevel,
    recentOutcome, recentOutcomeAt,
    weaponName, cs.sessionStats.killStreak,
  );
}

// ─── Equipment Bonus Helper ───────────────────────────────────────────────

function getEquipmentBonuses(gear: EquippedGear): { attack: number; defense: number; hp: number } {
  let attack = 0, defense = 0, hp = 0;
  for (const slot of ["weapon", "armor", "accessory", "shield"] as const) {
    const item = gear[slot];
    if (item?.equipmentData) {
      attack += item.equipmentData.attackBonus;
      defense += item.equipmentData.defenseBonus;
      hp += item.equipmentData.hpBonus;
    }
  }
  return { attack, defense, hp };
}

// ─── Constants ─────────────────────────────────────────────────────────────

const PLAYER_ATTACK_INTERVAL = 1.2;    // seconds between player swings
const DAMAGE_NUMBER_LIFETIME = 1.5;    // seconds before damage text fades
const DISENGAGE_RANGE = 6;             // tiles — flee this far and combat breaks
const CRIT_CHANCE = 0.10;
const CRIT_MULTIPLIER = 1.5;
const COMBO_BONUS = 0.05;              // +5% per consecutive hit
const DEATH_HP_FRACTION = 0.30;        // respawn at 30% HP
const DEATH_CURRENCY_LOSS = 0.10;      // lose 10% currency on death
const LEVELUP_HP_BONUS = 3;
const LEVELUP_ATK_BONUS = 1;
const LEVELUP_DEF_BONUS = 0.5;
const LEVELUP_XP_SCALE = 1.25;        // xpToNext grows 25% per level
const LEASH_RANGE = 12;                // tiles — creature resets if this far from spawn
const ENRAGE_HP_THRESHOLD = 0.20;      // creature enrages below 20% HP
const ENRAGE_ATK_BONUS = 1.4;          // 40% more attack when enraged
const ENRAGE_SPEED_BONUS = 0.7;        // 30% faster attacks when enraged
const DPS_WINDOW_SECONDS = 5;          // rolling DPS window

// ─── Dodge / Parry / Block Chances ─────────────────────────────────────────
// Based on gear defense + player level. Shield required for block.

function getDodgeChance(state: GameState): number {
  // Base 3% + 0.5% per level, capped at 15%
  return Math.min(0.15, 0.03 + state.player.combat.level * 0.005);
}

function getParryChance(state: GameState): number {
  // Only with a weapon equipped. Base 2% + 1% per 3 attack bonus
  const wpn = state.player.equipment.weapon;
  if (!wpn) return 0;
  const atkBonus = wpn.equipmentData?.attackBonus ?? 0;
  return Math.min(0.12, 0.02 + atkBonus * 0.003);
}

function getBlockChance(state: GameState): number {
  // Only with a shield equipped. Base 5% + 1% per 2 defense bonus
  const shd = state.player.equipment.shield;
  if (!shd) return 0;
  const defBonus = shd.equipmentData?.defenseBonus ?? 0;
  return Math.min(0.25, 0.05 + defBonus * 0.005);
}

// ─── Creature Attack Names ─────────────────────────────────────────────────

const CREATURE_ATTACK_NAMES: Record<string, string[]> = {
  dragon_fire:     ["Fire Breath", "Claw Swipe", "Tail Lash"],
  dragon_ice:      ["Frost Breath", "Ice Claw", "Tail Slam"],
  dragon_standard: ["Dragon Claw", "Bite", "Wing Buffet"],
  griffin_standard: ["Talon Strike", "Beak Thrust", "Wing Gust"],
  griffin:         ["Talon Strike", "Beak Thrust"],
  phoenix_standard: ["Flame Peck", "Ember Slash"],
  phoenix:         ["Flame Peck", "Ember Slash"],
  wolf_standard:   ["Bite", "Lunge", "Snap"],
  wolf_dire:       ["Savage Bite", "Pounce", "Rend"],
  wolf_arctic:     ["Frostbite", "Tackle", "Howl Strike"],
};

function getCreatureAttackName(speciesId: string): string {
  const names = CREATURE_ATTACK_NAMES[speciesId];
  if (!names || names.length === 0) return "Melee";
  return names[Math.floor(Math.random() * names.length)];
}

// Species categories for hostility rules
const DOMESTIC_SPECIES = new Set([
  "cat_house", "cat_orange_tabby", "cat_siamese", "cat_black",
  "dog_mixed", "dog_labrador", "dog_husky", "dog_corgi", "dog_shepherd",
  "pony_standard", "pony_shetland", "pony_unicorn",
]);

const FANTASY_SPECIES = new Set([
  "dragon_fire", "dragon_ice", "dragon_standard",
  "griffin_standard", "griffin",
  "phoenix_standard", "phoenix",
]);

const WOLF_SPECIES = new Set([
  "wolf_standard", "wolf_dire", "wolf_arctic",
]);

/** Training dummy species — immortal, no attack, no XP/loot, DPS meter */
export const TRAINING_DUMMY_SPECIES = new Set([
  "training_dummy_wooden", "training_dummy_straw",
  "training_dummy_iron", "training_dummy_armored",
]);

/** Check if a creature is a training dummy */
export function isTrainingDummy(creature: CreatureState): boolean {
  return TRAINING_DUMMY_SPECIES.has(creature.speciesId);
}

// ─── Aggro Range by Species Category ───────────────────────────────────────

function getAggroRange(speciesId: string): number {
  if (FANTASY_SPECIES.has(speciesId)) return 5;
  if (WOLF_SPECIES.has(speciesId)) return 4;
  return 3;
}

// ─── Creature Attack Interval ──────────────────────────────────────────────

function getCreatureAttackInterval(creature: CreatureState): number {
  // Fast creatures attack quicker: energy and agility reduce interval
  const speedFactor = (creature.genome.energy + creature.genome.agility) / 2;
  return 2.5 - speedFactor * 1.0; // range: 1.5s (fast) to 2.5s (slow)
}

// ─── Tile Distance ─────────────────────────────────────────────────────────

function tileDistance(
  a: { tileX: number; tileY: number },
  b: { tileX: number; tileY: number },
): number {
  const dx = a.tileX - b.tileX;
  const dy = a.tileY - b.tileY;
  return Math.sqrt(dx * dx + dy * dy);
}

// ─── Hostility ─────────────────────────────────────────────────────────────

/**
 * Determine whether a creature is hostile in the current zone.
 * Domestic animals are never hostile. Fantasy creatures are always hostile
 * in any danger zone. Wolves become hostile at dangerLevel >= 1.
 * Owned creatures (pets) are never hostile regardless of species.
 */
export function isHostile(creature: CreatureState, dangerLevel: number): boolean {
  if (dangerLevel <= 0) return false;
  if (creature.ownerId !== null) return false;
  if (DOMESTIC_SPECIES.has(creature.speciesId)) return false;

  if (FANTASY_SPECIES.has(creature.speciesId)) return true;

  if (WOLF_SPECIES.has(creature.speciesId)) {
    // Wolves with very high courage are hostile even at danger 1;
    // otherwise they need danger >= 2 to attack
    return creature.genome.courage > 0.6 || dangerLevel >= 2;
  }

  // Generic creatures: hostile if courage + packInstinct weighted check passes
  const hostilityThreshold = 1.0 - dangerLevel * 0.3; // d1: 0.7, d2: 0.4, d3: 0.1
  return (creature.genome.courage * 0.6 + creature.genome.packInstinct * 0.4) > hostilityThreshold;
}

// ─── Aggro Detection ───────────────────────────────────────────────────────

/**
 * Check whether a hostile creature should aggro onto the player.
 * Returns true if the creature is hostile and the player is within aggro range.
 */
export function checkAggro(
  creature: CreatureState,
  playerPos: { tileX: number; tileY: number },
  dangerLevel: number,
): boolean {
  if (!isHostile(creature, dangerLevel)) return false;
  if (creature.behaviorState.current === "sleep" || creature.behaviorState.current === "mounted") return false;

  const range = getAggroRange(creature.speciesId);
  return tileDistance(creature.position, playerPos) <= range;
}

// ─── Creature Combat Stats ─────────────────────────────────────────────────

/**
 * Derive combat stats from genome traits and zone danger level.
 * Higher danger zones produce tougher creatures. Courage and energy
 * contribute to attack; stamina and packInstinct to defense and HP.
 */
export function getCreatureCombatStats(
  creature: CreatureState,
  dangerLevel: number,
): { hp: number; maxHp: number; attack: number; defense: number } {
  const g = creature.genome;
  const dangerScale = 1 + (dangerLevel - 1) * 0.5; // d1: 1.0, d2: 1.5, d3: 2.0

  const baseHp = 10 + g.stamina * 15 + g.packInstinct * 5;
  const baseAtk = 2 + g.courage * 4 + g.energy * 3;
  const baseDef = 1 + g.stamina * 2 + g.territoriality * 2;

  // Fantasy creatures are inherently stronger
  const fantasticBonus = FANTASY_SPECIES.has(creature.speciesId) ? 1.5 : 1.0;

  const hp = Math.round(baseHp * dangerScale * fantasticBonus);
  const attack = Math.round(baseAtk * dangerScale * fantasticBonus);
  const defense = Math.round(baseDef * dangerScale * fantasticBonus);

  return { hp, maxHp: hp, attack, defense };
}

// ─── Combat Initiation ─────────────────────────────────────────────────────

/**
 * Start combat with a creature. Initializes creature HP tracking,
 * sets player combat state to active, and resets combo.
 */
export function startCombat(state: GameState, creatureId: string): void {
  const cs = state.player.combatState;
  if (cs.active) return; // already fighting

  const creature = state.creatures.find(c => c.id === creatureId);
  if (!creature) return;

  // Determine danger level from current zone
  const dangerLevel = getCurrentDangerLevel(state);

  // Initialize creature HP
  const stats = getCreatureCombatStats(creature, dangerLevel);
  creatureCombatHp.set(creatureId, { hp: stats.maxHp, maxHp: stats.maxHp });

  // Record spawn position for leash mechanic (if not already set)
  if (creature.behaviorState.spawnTileX === undefined) {
    creature.behaviorState.spawnTileX = creature.position.tileX;
    creature.behaviorState.spawnTileY = creature.position.tileY;
  }

  // Activate combat
  const now = performance.now() / 1000;
  cs.active = true;
  cs.targetId = creatureId;
  cs.lastAttackTime = now;
  cs.lastHitTime = now;
  cs.combo = 0;
  cs.strikeQueued = false;
  cs.strikeQueuedAt = 0;
  cs.staggerTimer = 0;
  cs.damageNumbers = [];

  // Aggro indicator — the "!" moment. Heart-jump territory.
  // Deduplicate: remove any existing indicator for this creature first
  state.aggroIndicators = state.aggroIndicators.filter(a => a.creatureId !== creatureId);
  state.aggroIndicators.push({ creatureId, time: now });

  // Reset session stats for this encounter
  cs.sessionStats.combatStartedAt = now;
  cs.sessionStats.totalDamageDealt = 0;
  cs.sessionStats.totalDamageTaken = 0;
  cs.sessionStats.dpsWindow = [];
  cs.sessionStats.currentDps = 0;
  cs.sessionStats.dodgeCount = 0;
  cs.sessionStats.parryCount = 0;
  cs.sessionStats.blockCount = 0;
  // killStreak and totalKills persist across encounters — not reset here

  // Exit build mode if active — combat takes priority
  if (state.ui.editMode) {
    state.ui.editMode = false;
    state.ui.selectedFurniture = null;
    state.ui.furnitureContextId = null;
    state.ui.placingFurnitureType = null;
    state.ui.movingFurnitureId = null;
    state.ui.movingOriginalTile = null;
    state.ui.inventoryOpen = false;
    state.ui.pendingBuildAction = null;
  }
  // Close emote wheel if open — combat overrides social actions
  if (state.player.emoteWheel.open) {
    state.player.emoteWheel.open = false;
    state.player.emoteWheel.selectedIndex = -1;
  }
  // Enter combat ready stance — player looks ALERT
  state.player.animState = "combat_idle";
  state.ui.combatStance = true;
  // Stop any in-progress movement
  state.player.targetPosition = undefined;
  state.player.movementQueue = [];

  // Training dummies don't chase — they stand still and take it
  if (isTrainingDummy(creature)) {
    // Initialize DPS meter
    state.trainingDummy.dpsMeter = {
      targetId: creatureId,
      totalDamage: 0,
      startTime: now,
      currentDPS: 0,
      peakDPS: 0,
      biggestHit: 0,
      combo: 0,
      maxCombo: 0,
      damageWindow: [],
      visible: true,
    };
  } else {
    // The creature begins its pursuit — switch from whatever idle state to chase.
    // This overrides the behavior AI so the creature relentlessly tracks the player.
    creature.behaviorState.current = "chase";
    creature.behaviorState.stateTime = 0;
    creature.behaviorState.nextTransition = 9999; // combat system controls duration
    creature.movementQueue = [];
  }
}

// ─── Combat Update ─────────────────────────────────────────────────────────

/**
 * The main combat tick. Called every frame.
 * Handles auto-attacks for both player and creature, damage calculation,
 * death checks, XP awards, and disengage logic.
 */
export function updateCombat(state: GameState, dt: number): ImpactEvent[] {
  const impacts: ImpactEvent[] = [];
  const cs = state.player.combatState;

  // Decay stagger timer BEFORE the early return — stagger must fade even
  // if combat ended (e.g. creature died mid-stagger).
  if (cs.staggerTimer > 0) {
    cs.staggerTimer = Math.max(0, cs.staggerTimer - dt);
  }

  if (!cs.active || !cs.targetId) return impacts;

  const now = performance.now() / 1000;
  const creature = state.creatures.find(c => c.id === cs.targetId);
  const creatureHp = creatureCombatHp.get(cs.targetId!);

  // Target gone — end combat
  if (!creature || !creatureHp) {
    endCombat(state);
    return impacts;
  }

  const dangerLevel = getCurrentDangerLevel(state);

  // Disengage check — player moved too far away
  if (tileDistance(state.player.position, creature.position) > DISENGAGE_RANGE) {
    // Check if this was a narrow escape (low HP)
    const hpRat = state.player.combat.hp / state.player.combat.maxHp;
    recentOutcome = hpRat < 0.25 ? "narrow_escape" : "retreat";
    recentOutcomeAt = performance.now() / 1000;
    endCombat(state);
    return impacts;
  }

  const creatureStats = getCreatureCombatStats(creature, dangerLevel);
  const isDummy = isTrainingDummy(creature);

  // ── Rolling DPS calculation ─────────────────────────────────────────
  {
    const ss = cs.sessionStats;
    const cutoff = now - DPS_WINDOW_SECONDS;
    // Prune samples older than the window
    while (ss.dpsWindow.length > 0 && ss.dpsWindow[0].timestamp < cutoff) {
      ss.dpsWindow.shift();
    }
    // Compute DPS from remaining samples
    if (ss.dpsWindow.length > 0) {
      const totalInWindow = ss.dpsWindow.reduce((sum, s) => sum + s.damage, 0);
      const windowSpan = Math.max(0.5, now - ss.dpsWindow[0].timestamp);
      ss.currentDps = Math.round(totalInWindow / windowSpan * 10) / 10;
    } else {
      ss.currentDps = 0;
    }
    // Decay kill streak display timer
    if (ss.killStreakShowTime > 0) ss.killStreakShowTime -= dt;
  }

  // ── Buff modifiers — computed once per frame ────────────────────────
  const buffMods = getBuffModifiers(state);

  // ── Player attacks creature (frame-synced) ──────────────────────────
  // Stun prevents all player actions
  if (buffMods.isStunned) {
    // Can't act — but creature can still hit us, so fall through
  }

  // Phase 1: Attack interval triggers animation + queues strike
  // Haste/slow modifies the effective attack interval; momentum adds subtle speed
  const momentumSpeed = getMomentumSpeedMultiplier(momentum);
  const effectiveAttackInterval = PLAYER_ATTACK_INTERVAL / (buffMods.attackSpeedMultiplier * momentumSpeed);
  if (!buffMods.isStunned && !cs.strikeQueued && now - cs.lastAttackTime >= effectiveAttackInterval) {
    cs.lastAttackTime = now;
    cs.strikeQueued = true;
    cs.strikeQueuedAt = now;
    // Trigger attack animation — damage lands on frame 1 (0.1s later)
    state.player.animState = "combat_attack";
    // Whoosh — weapon-class-aware swing
    const swingWeapon = state.player.equipment.weapon?.equipmentData?.weaponVisual?.class ?? null;
    audioEngine?.playCombatSwing?.(swingWeapon);
  }

  // ── Momentum ODE integration — continuous dynamics, not discrete combo ──
  updateMomentum(momentum, dt, true);

  // Phase 2: Damage lands when strike frame arrives (synced to visual)
  if (cs.strikeQueued && now - cs.strikeQueuedAt >= STRIKE_FRAME_DELAY) {
    cs.strikeQueued = false;

    // Momentum replaces flat combo: living multiplier from continuous ODE
    const momentumMult = getMomentumDamageMultiplier(momentum);
    const momentumCritBonus = getMomentumCritBonus(momentum);
    const weaponName = state.player.equipment.weapon?.name;
    const attunementBonus = getAttunementDamageBonus(attunement, weaponName);

    const gearBonus = getEquipmentBonuses(state.player.equipment);
    let damage = calcDamage(state.player.combat.attack + gearBonus.attack + buffMods.attackBonus, creatureStats.defense);
    let isCrit = false;

    // Crit chance: base + momentum bonus (replaces flat 10%)
    if (Math.random() < CRIT_CHANCE + momentumCritBonus) {
      damage = Math.round(damage * CRIT_MULTIPLIER);
      isCrit = true;
    }

    // Apply momentum multiplier + attunement bonus (replaces flat combo)
    damage = Math.max(1, Math.round(damage * momentumMult * (1 + attunementBonus)));
    creatureHp.hp -= damage;

    // Feed the momentum ODE — successful hit impulse
    applyHitImpulse(momentum, isCrit);
    cs.combo++; // Keep combo count for UI/legacy compatibility

    // Record weapon attunement hit
    if (weaponName) recordWeaponHit(attunement, weaponName, now);

    // Track combat style
    recordStyleData(styleTracker, damage, 0, state.player.combat.maxHp);

    // Track DPS + total damage dealt
    cs.sessionStats.totalDamageDealt += damage;
    cs.sessionStats.dpsWindow.push({ damage, timestamp: now });

    // Record hit time for visual flash on the creature
    state.creatureHitTimes.set(creature.id, now);

    // Impact sound — weapon-class + target-material aware
    const hitWeapon = state.player.equipment.weapon?.equipmentData?.weaponVisual?.class ?? null;
    const targetMat = creature.speciesId.includes("iron") || creature.speciesId.includes("armored")
      ? "metal"
      : creature.speciesId.includes("straw") ? "organic"
      : creature.speciesId.includes("stone") || creature.speciesId.includes("golem") ? "stone"
      : creature.speciesId.includes("wood") || creature.speciesId.includes("dummy") ? "wood"
      : "organic"; // default for living creatures
    const hitIntensity = isCrit ? 0.9 : Math.min(0.85, 0.5 + cs.combo * 0.05);
    audioEngine?.playCombatHit?.(hitWeapon, targetMat, isCrit, hitIntensity);

    pushDamageNumber(cs, damage, creature.position, false, isCrit);
    addCombatLogEntry(`You hit ${creature.name} for ${damage}${isCrit ? " (CRIT)" : ""}`, isCrit ? "#ffd040" : "#e0c080");

    // Screen shake — accumulate so rapid hits feel heavier, cap at 12
    state.screenShake.intensity = Math.min(12, state.screenShake.intensity + (isCrit ? 6 : 3));
    state.screenShake.decay = 12;
    state.screenShake.phase = 0;  // reset phase so shake starts at peak displacement

    // Impact particles at creature position
    impacts.push({ wx: creature.position.tileX, wy: creature.position.tileY, wz: creature.position.elevation, isCrit });

    // ── Training dummy DPS meter update ──────────────────────────────
    if (isDummy && state.trainingDummy.dpsMeter) {
      const meter = state.trainingDummy.dpsMeter;
      meter.totalDamage += damage;
      meter.combo = cs.combo;
      if (cs.combo > meter.maxCombo) meter.maxCombo = cs.combo;
      if (damage > meter.biggestHit) meter.biggestHit = damage;
      meter.damageWindow.push([now, damage]);
      // Trim window to last 5 seconds
      meter.damageWindow = meter.damageWindow.filter(([t]) => now - t <= 5);
      const windowDamage = meter.damageWindow.reduce((sum, [, d]) => sum + d, 0);
      const windowTime = meter.damageWindow.length > 0 ? Math.max(1, now - meter.damageWindow[0][0]) : 1;
      meter.currentDPS = windowDamage / windowTime;
      if (meter.currentDPS > meter.peakDPS) meter.peakDPS = meter.currentDPS;
      // Track hits/damage per dummy
      const cid = creature.id;
      state.trainingDummy.hitsAbsorbed[cid] = (state.trainingDummy.hitsAbsorbed[cid] ?? 0) + 1;
      state.trainingDummy.damageAbsorbed[cid] = (state.trainingDummy.damageAbsorbed[cid] ?? 0) + damage;
    }

    // Creature dies — training dummies reset HP instead of dying
    if (creatureHp.hp <= 0) {
      if (isDummy) {
        // Dummy "resets" — HP back to full, combat continues
        creatureHp.hp = creatureHp.maxHp;
        addToast(state, `${creature.name} resets!`, "info", 1.5);
        addCombatLogEntry(`${creature.name} absorbs the blow and resets.`, "#a0a0a0");
        // Small visual feedback
        state.screenShake.intensity = Math.min(6, state.screenShake.intensity + 2);
        state.screenShake.decay = 10;
      } else {
        // Big shake on kill
        state.screenShake.intensity = 8;
        state.screenShake.decay = 8;
        state.screenShake.phase = 0;
        onCreatureDefeated(state, creature, dangerLevel);
        return impacts;
      }
    }
  }

  // ── Creature attacks player ──────────────────────────────────────────
  // ── Creature enrage — below 20% HP, creatures become feral ────────
  const hpRatio = creatureHp.hp / creatureHp.maxHp;
  const isEnraged = hpRatio > 0 && hpRatio < ENRAGE_HP_THRESHOLD;
  const enrageAtkMult = isEnraged ? ENRAGE_ATK_BONUS : 1.0;
  const enrageSpeedMult = isEnraged ? ENRAGE_SPEED_BONUS : 1.0;

  // ── Creature leash — too far from spawn, reset and disengage ──────
  if (creature.behaviorState.spawnTileX !== undefined && creature.behaviorState.spawnTileY !== undefined) {
    const distFromSpawn = tileDistance(
      creature.position,
      { tileX: creature.behaviorState.spawnTileX, tileY: creature.behaviorState.spawnTileY },
    );
    if (distFromSpawn > LEASH_RANGE) {
      // Creature evades — reset HP and disengage
      creatureHp.hp = creatureHp.maxHp;
      creature.position.tileX = creature.behaviorState.spawnTileX;
      creature.position.tileY = creature.behaviorState.spawnTileY;
      addCombatLogEntry(`${creature.name} evades and resets!`, "#c0c0c0");
      endCombat(state);
      return impacts;
    }
  }

  const meleeRange = 2;
  const creatureToPlayerDist = tileDistance(creature.position, state.player.position);
  const creatureInterval = isDummy ? 9999 : getCreatureAttackInterval(creature) * enrageSpeedMult;
  if (!isDummy && creatureToPlayerDist <= meleeRange && now - cs.lastHitTime >= creatureInterval) {
    cs.lastHitTime = now;

    // ── Dodge / Parry / Block — avoidance rolls ───────────────────
    const dodgeRoll = Math.random();
    const parryRoll = Math.random();
    const blockRoll = Math.random();

    if (dodgeRoll < getDodgeChance(state) && !buffMods.isStunned) {
      // DODGE — complete avoidance
      cs.sessionStats.dodgeCount++;
      pushDamageNumber(cs, 0, state.player.position, true, false, "dodge");
      addCombatLogEntry("You dodge the attack!", "#80c0e0");
      audioEngine?.playInteraction?.("combat_dodge");
      return impacts;
    }

    if (parryRoll < getParryChance(state) && !buffMods.isStunned) {
      // PARRY — deflect with weapon, counter-opportunity
      cs.sessionStats.parryCount++;
      pushDamageNumber(cs, 0, state.player.position, true, false, "dodge"); // visual: "PARRY"
      addCombatLogEntry("You parry the attack!", "#d0a040");
      audioEngine?.playInteraction?.("combat_parry");
      // Parry grants a small counter-window: reduce next attack interval
      cs.lastAttackTime -= 0.3;
      return impacts;
    }

    const gearDef = getEquipmentBonuses(state.player.equipment);
    let damage = calcDamage(Math.round(creatureStats.attack * enrageAtkMult), state.player.combat.defense + gearDef.defense + buffMods.defenseBonus);
    let isCrit = false;

    if (Math.random() < CRIT_CHANCE) {
      damage = Math.round(damage * CRIT_MULTIPLIER);
      isCrit = true;
    }

    damage = Math.max(1, damage);

    // Block check — reduces damage by 30-50% based on shield quality
    if (blockRoll < getBlockChance(state) && !buffMods.isStunned) {
      const shd = state.player.equipment.shield;
      const blockPct = 0.30 + ((shd?.equipmentData?.defenseBonus ?? 0) * 0.02);
      const blocked = Math.round(damage * Math.min(0.50, blockPct));
      damage = Math.max(1, damage - blocked);
      cs.sessionStats.blockCount++;
      pushDamageNumber(cs, blocked, state.player.position, true, false, "block");
      addCombatLogEntry(`You block ${blocked} damage!`, "#8888aa");
      audioEngine?.playInteraction?.("combat_block");
    }

    // Apply damage-taken multiplier (e.g. Shadow Mark increases damage)
    damage = Math.max(1, Math.round(damage * buffMods.damageTakenMultiplier));

    // Apply damage reduction from Fortify
    if (buffMods.damageReduction > 0) {
      damage = Math.max(1, Math.round(damage * (1 - buffMods.damageReduction)));
    }

    // Shield absorption — consumes shield buff HP before touching player HP
    if (buffMods.shieldHp > 0) {
      damage = absorbDamage(state, damage);
    }

    // Duel safety — clamp damage so HP never drops below 1 during a duel
    damage = duelDamageClamp(state, damage);
    state.player.combat.hp -= damage;

    // Track damage taken
    cs.sessionStats.totalDamageTaken += damage;

    // Death recap — record damage source for "what killed you" panel
    if (damage > 0) {
      const abilityName = isEnraged
        ? getCreatureAttackName(creature.speciesId) + " (Enraged)"
        : getCreatureAttackName(creature.speciesId);
      const recap = state.player.deathRecap;
      recap.entries.push({
        sourceName: creature.name,
        damage,
        timestamp: now,
        isCrit,
        abilityName,
      });
      // Keep only last 10 entries
      if (recap.entries.length > 10) recap.entries.shift();
    }

    // Thorns reflection — damage the creature back
    if (buffMods.thornsReflect > 0 && damage > 0) {
      const thornsDmg = Math.max(1, Math.round(buffMods.thornsReflect));
      creatureHp.hp -= thornsDmg;
      state.creatureHitTimes.set(creature.id, now);
    }

    // The player takes the hit — deep, visceral thud
    audioEngine?.playInteraction?.("combat_player_hurt");

    // Timestamp for damage vignette overlay
    state.lastPlayerHitTime = performance.now() / 1000;

    // Momentum negative impulse — proportional to damage, not binary reset.
    // A skilled player can recover from a single hit. Sustained damage collapses momentum.
    const dmgRatio = damage / Math.max(1, state.player.combat.maxHp);
    applyDamageTakenImpulse(momentum, dmgRatio);

    // Track combat style — damage taken
    recordStyleData(styleTracker, 0, damage, state.player.combat.maxHp);

    // Show "COMBO BROKEN" if momentum collapses from flow to non-flow
    if (cs.combo >= 3 && momentum.value < 0.2) {
      pushDamageNumber(cs, 0, state.player.position, true, false, "combo_break");
    }
    cs.combo = 0;

    // Stagger — player flinches from the hit
    cs.staggerTimer = 0.25;

    // Screen shake — getting hit hurts (accumulates)
    state.screenShake.intensity = Math.min(12, state.screenShake.intensity + (isCrit ? 7 : 5));
    state.screenShake.decay = 10;
    state.screenShake.phase = 0;  // reset phase so shake starts at peak

    // Impact particles at player position
    impacts.push({ wx: state.player.position.tileX, wy: state.player.position.tileY, wz: state.player.position.elevation, isCrit });

    pushDamageNumber(cs, damage, state.player.position, true, isCrit);
    {
      const atkName = isEnraged ? getCreatureAttackName(creature.speciesId) : getCreatureAttackName(creature.speciesId);
      const enrageTag = isEnraged ? " [ENRAGED]" : "";
      addCombatLogEntry(
        `${creature.name} ${atkName} you for ${damage}${isCrit ? " (CRIT)" : ""}${enrageTag}`,
        isCrit ? "#ff6040" : "#e06040",
      );
    }

    // Player dies
    if (state.player.combat.hp <= 0) {
      onPlayerDefeated(state);
      return impacts;
    }

    // Thorns killed the creature
    if (creatureHp.hp <= 0) {
      state.screenShake.intensity = 8;
      state.screenShake.decay = 8;
      state.screenShake.phase = 0;
      onCreatureDefeated(state, creature, dangerLevel);
      return impacts;
    }
  }

  // ── Clean up expired damage numbers ──────────────────────────────────
  cs.damageNumbers = cs.damageNumbers.filter(
    dn => now - dn.createdAt < DAMAGE_NUMBER_LIFETIME,
  );

  return impacts;
}

// ─── Damage Calculation ────────────────────────────────────────────────────

function calcDamage(attack: number, defense: number): number {
  const base = Math.max(1, attack - defense / 2);
  const variance = 0.2;
  const roll = 1 + (Math.random() * 2 - 1) * variance; // 0.8 to 1.2
  return Math.max(1, Math.round(base * roll));
}

// ─── Damage Number Helper ──────────────────────────────────────────────────

function pushDamageNumber(
  cs: { damageNumbers: DamageNumber[] },
  value: number,
  position: { tileX: number; tileY: number },
  isPlayerDamage: boolean,
  isCrit: boolean,
  overrideDamageType?: DamageType,
): void {
  // Screen coords derived from tile position with slight random offset
  const screenX = (position.tileX - position.tileY) * 32 + (Math.random() - 0.5) * 16;
  const screenY = (position.tileX + position.tileY) * 16 - 40 + (Math.random() - 0.5) * 8;

  cs.damageNumbers.push({
    value,
    x: screenX,
    y: screenY,
    createdAt: performance.now() / 1000,
    isPlayerDamage,
    isCrit,
    damageType: overrideDamageType ?? (value === 0 ? "miss" : "physical"),
    angle: -Math.PI / 2 + (Math.random() - 0.5) * 0.6,  // roughly upward with slight spread
  });
}

// ─── Creature Defeated ─────────────────────────────────────────────────────

function onCreatureDefeated(state: GameState, creature: CreatureState, dangerLevel: number): void {
  // Death knell — descending sweep as the creature falls
  audioEngine?.playInteraction?.("combat_death");

  // Victory flash — brief screen glow
  state.screenShake.intensity = Math.max(state.screenShake.intensity, 3);

  // Pretty creature name
  const creatureName = creature.speciesId.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
  addToast(state, `Defeated ${creatureName}!`, "achievement", 2.5);
  addCombatLogEntry(`${creatureName} has been slain!`, "#60e060");

  // Kill streak tracking
  const stats = state.player.combatState.sessionStats;
  stats.killStreak++;
  stats.totalKills++;
  if (stats.killStreak > stats.bestKillStreak) stats.bestKillStreak = stats.killStreak;
  if (stats.killStreak >= 3) {
    stats.killStreakShowTime = 3.0; // show banner for 3 seconds
    const streakNames = ["", "", "", "Killing Spree", "Rampage", "Unstoppable", "Godlike", "Legendary"];
    const streakName = streakNames[Math.min(stats.killStreak, streakNames.length - 1)] || `${stats.killStreak}x Streak`;
    addToast(state, streakName + "!", "achievement", 2);
    addCombatLogEntry(`${streakName}! (${stats.killStreak} kills)`, "#ffd040");
  }

  // ── Novelty-weighted XP — prediction error drives growth ────────
  // First encounter with a species: full XP. Same wolves forever: diminishing.
  // Diverse combat rewarded. Mirrors BCM novelty gating in the Aeterna.
  const baseXp = 10 + dangerLevel * 8;
  const fantasyBonus = FANTASY_SPECIES.has(creature.speciesId) ? 2.0 : 1.0;
  const noveltyMult = getNoveltyMultiplier(familiarity, creature.speciesId);
  const xpGain = Math.round(baseXp * fantasyBonus * noveltyMult);

  // Record kill for familiarity tracking (before adding XP)
  recordKill(familiarity, creature.speciesId);

  // Emit a combat XP event so the UI can render the novelty multiplier
  // alongside the floating "+X XP" text. Combined novelty × fantasy is
  // passed so the displayed multiplier reflects the total modifier above
  // the base. Effective mult = xpGain / baseXp.
  const effectiveMult = baseXp > 0 ? xpGain / baseXp : 1;
  _combatXpQueue.push({ value: xpGain, noveltyMult: effectiveMult });

  state.player.combat.xp += xpGain;

  // Record outcome for Aeterna awareness
  recentOutcome = "victory";
  recentOutcomeAt = performance.now() / 1000;

  // Currency reward
  const currencyGain = Math.round(5 + dangerLevel * 5 * fantasyBonus);
  state.player.currency += currencyGain;

  // Reputation reward
  state.player.reputation += Math.round(1 + dangerLevel);

  // Loot drop — chance scales with danger level
  const loot = rollLootDrop(creature.speciesId, dangerLevel);
  if (loot) {
    state.player.inventory.push(loot);
    // Notify player of loot (legacy notification + toast)
    state.ui.notifications.push({
      id: `loot_${loot.id}`,
      text: `Found: ${loot.name}`,
      expiresAt: Date.now() + 4000,
    });
    addToast(state, "Found: " + loot.name, "loot");
    // LOOT CHIME — Pavlovian reward sound
    audioEngine?.playInteraction?.("loot_drop");
    addCombatLogEntry(`Looted: ${loot.name}`, loot.rarity === "rare" ? "#4888d8" : loot.rarity === "legendary" ? "#a050d0" : loot.rarity === "uncommon" ? "#48a848" : "#b8b0a0");
  }

  // Notify XP/currency gained
  state.ui.notifications.push({
    id: `combat_${Date.now()}`,
    text: `+${xpGain} XP  +${currencyGain} gold  +${Math.round(1 + dangerLevel)} rep`,
    expiresAt: Date.now() + 3000,
  });

  // Snapshot dying creature for death animation before removing from world
  state.dyingCreatures.push({
    creature: { ...creature },
    startTime: performance.now() / 1000,
    duration: 1.2,
  });

  // Remove creature from the world
  state.creatures = state.creatures.filter(c => c.id !== creature.id);
  creatureCombatHp.delete(creature.id);
  state.creatureHitTimes.delete(creature.id);
  cleanupCreature(creature.id);
  // NOTE: Don't call cleanupCreatureAttention here — the dying creature
  // continues rendering for 1.2s and would lazily re-create the entry.
  // Attention cleanup happens when the death animation expires (loop.ts).

  // Check for level up
  const oldLevel = state.player.combat.level;
  while (checkLevelUp(state.player.combat)) {
    applyLevelUp(state.player.combat);
  }
  if (state.player.combat.level > oldLevel) {
    // Grant skill points for each level gained
    const levelsGained = state.player.combat.level - oldLevel;
    state.player.skillTree.points += levelsGained;

    // Increase max ember with level
    state.player.combat.ember.max = 20 + (state.player.combat.level - 1) * 2;
    state.player.combat.ember.current = state.player.combat.ember.max; // full EP on level up

    state.ui.notifications.push({
      id: `levelup_${Date.now()}`,
      text: `Level Up! You are now level ${state.player.combat.level}`,
      expiresAt: Date.now() + 5000,
    });
    addToast(state, "Level Up! Now level " + state.player.combat.level, "levelup");
    if (levelsGained > 0) {
      addToast(state, `+${levelsGained} Skill Point${levelsGained > 1 ? "s" : ""}!`, "achievement", 3);
    }
    // LEVEL UP FANFARE — the most important sound in the game
    audioEngine?.playInteraction?.("level_up");
  }

  endCombat(state);
}

// ─── Player Defeated ───────────────────────────────────────────────────────

function onPlayerDefeated(state: GameState): void {
  // Record outcome for Aeterna awareness — the Aeterna knows you fell
  recentOutcome = "defeat";
  recentOutcomeAt = performance.now() / 1000;

  // Defeat sound + visual
  audioEngine?.playInteraction?.("combat_death");
  state.screenShake.intensity = 12;
  state.screenShake.decay = 4;
  state.screenShake.phase = 0;

  // PvE currency penalty — PvP deaths use a different penalty (XP in killPlayer)
  const loss = Math.floor(state.player.currency * DEATH_CURRENCY_LOSS);
  state.player.currency = Math.max(0, state.player.currency - loss);
  if (loss > 0) addToast(state, `Lost ${loss} gold`, "info", 4);
  addCombatLogEntry("You have been defeated", "#e04040");

  // Kill streak broken
  state.player.combatState.sessionStats.killStreak = 0;

  // Death recap — auto-show and mark death time
  state.player.deathRecap.visible = true;
  state.player.deathRecap.diedAt = performance.now() / 1000;

  // Delegate to unified death system — handles ghost mode, respawn timer, XP penalty
  // killPlayer sets isDead, ghostMode, respawnTimer, deathPenalty, clears combat state
  killPlayer(state, false);

  // Clean up creature HP tracking for the target
  if (state.player.combatState.targetId) {
    creatureCombatHp.delete(state.player.combatState.targetId);
  }

  endCombat(state);
}

// ─── End Combat ────────────────────────────────────────────────────────────

function endCombat(state: GameState): void {
  const cs = state.player.combatState;

  // Reset momentum for next encounter — fatigue persists across fights
  resetMomentum(momentum);

  // Revert any chasing creature back to idle — the hunt is over.
  // The creature might have been killed (already removed from state.creatures)
  // or the player disengaged. Either way, any surviving creature in chase
  // with a matching targetId should stand down.
  if (cs.targetId) {
    const creature = state.creatures.find(c => c.id === cs.targetId);
    if (creature && creature.behaviorState.current === "chase") {
      creature.behaviorState.current = "idle";
      creature.behaviorState.stateTime = 0;
      creature.behaviorState.nextTransition = 3 + Math.random() * 5;
      creature.movementQueue = [];
    }
  }

  // DPS meter final summary for training dummies
  const meter = state.trainingDummy.dpsMeter;
  if (meter && meter.totalDamage > 0) {
    const elapsed = Math.max(1, performance.now() / 1000 - meter.startTime);
    const avgDPS = Math.round(meter.totalDamage / elapsed);
    addToast(state, `Session: ${meter.totalDamage} dmg | ${avgDPS} avg DPS | ${meter.maxCombo} max combo`, "info", 4);
    addCombatLogEntry(`Training complete — ${meter.totalDamage} total damage, ${Math.round(meter.peakDPS)} peak DPS, ${meter.maxCombo} max combo`, "#80c0e0");
  }
  state.trainingDummy.dpsMeter = null;

  cs.active = false;
  cs.targetId = null;
  cs.combo = 0;
  cs.strikeQueued = false;
  cs.strikeQueuedAt = 0;
  cs.staggerTimer = 0;
  cs.damageNumbers = [];
  // Return to combat_idle if player is holding stance, else relax to idle
  state.player.animState = state.ui.combatStance ? "combat_idle" : "idle";
}

// ─── XP / Leveling ─────────────────────────────────────────────────────────

/** Returns true if the player has enough XP to level up */
export function checkLevelUp(stats: CombatStats): boolean {
  return stats.xp >= stats.xpToNext;
}

/** Apply a single level up: boost stats, consume XP, raise threshold */
export function applyLevelUp(stats: CombatStats): void {
  stats.xp -= stats.xpToNext;
  stats.level++;
  stats.maxHp += LEVELUP_HP_BONUS;
  stats.hp = stats.maxHp; // full heal on level up
  stats.attack += LEVELUP_ATK_BONUS;
  stats.defense += LEVELUP_DEF_BONUS;
  stats.xpToNext = Math.round(stats.xpToNext * LEVELUP_XP_SCALE);
}

// ─── Aggro Scanning ────────────────────────────────────────────────────────

/**
 * Scan all creatures in the current room for aggro.
 * If combat isn't already active, start combat with the closest hostile creature.
 */
export function scanForAggro(state: GameState, dangerLevel: number): void {
  if (state.player.combatState.active) return;
  if (state.player.combat.hp <= 0) return;

  const playerPos = state.player.position;
  let closest: CreatureState | null = null;
  let closestDist = Infinity;

  for (const creature of state.creatures) {
    if (!checkAggro(creature, playerPos, dangerLevel)) continue;

    const dist = tileDistance(creature.position, playerPos);
    if (dist < closestDist) {
      closestDist = dist;
      closest = creature;
    }
  }

  if (closest) {
    startCombat(state, closest.id);
  }
}

// ─── Zone Danger Helper ────────────────────────────────────────────────────

/**
 * Read the current zone's danger level from world state.
 * Falls back to 0 (safe) if no zone is active.
 */
function getCurrentDangerLevel(state: GameState): number {
  const zoneId = state.world?.currentZoneId;
  if (!zoneId) return 0;

  const zones = state.world.zones;
  if (!zones) return 0;
  for (let i = 0; i < zones.length; i++) {
    if (zones[i].id === zoneId) return zones[i].dangerLevel;
  }
  return 0;
}

// ─── Public HP Access ──────────────────────────────────────────────────────

/** Get the tracked combat HP for a creature, or null if not in combat */
export function getCreatureHp(creatureId: string): { hp: number; maxHp: number } | null {
  return creatureCombatHp.get(creatureId) ?? null;
}

/** Clear all tracked creature HP (call on zone transitions) */
export function clearCreatureCombatHp(): void {
  creatureCombatHp.clear();
}

// ─── HP Regeneration ──────────────────────────────────────────────────────

const HP_REGEN_RATE = 0.5; // HP per second when out of combat
let _regenAccum = 0;

/** Regenerate player HP when not in combat. Call each frame with dt in seconds. */
export function regenHp(state: GameState, dt: number): void {
  if (state.player.combatState.active) { _regenAccum = 0; return; }
  const c = state.player.combat;
  if (c.hp >= c.maxHp) return;
  _regenAccum += dt * HP_REGEN_RATE;
  if (_regenAccum >= 1) {
    const heal = Math.floor(_regenAccum);
    c.hp = Math.min(c.maxHp, c.hp + heal);
    _regenAccum -= heal;
  }
}

// ─── Loot System ──────────────────────────────────────────────────────────

import type { InventoryItem, EquipmentData } from "../engine/state.js";

let _lootCounter = 0;

interface LootEntry {
  name: string;
  slot: EquipmentData["slot"];
  attackBonus: number;
  defenseBonus: number;
  hpBonus: number;
  rarity: InventoryItem["rarity"];
  description: string;
  levelReq: number;
}

const LOOT_TABLE: Record<number, LootEntry[]> = {
  1: [
    { name: "Worn Leather Vest", slot: "armor", attackBonus: 0, defenseBonus: 2, hpBonus: 3, rarity: "common", description: "Scuffed but sturdy. Smells of woodsmoke.", levelReq: 1 },
    { name: "Whittled Staff", slot: "weapon", attackBonus: 2, defenseBonus: 0, hpBonus: 0, rarity: "common", description: "A walking stick that hits surprisingly hard.", levelReq: 1 },
    { name: "Woodsman's Hatchet", slot: "weapon", attackBonus: 3, defenseBonus: 0, hpBonus: 0, rarity: "common", description: "Nicks in the blade from years of honest work.", levelReq: 1 },
    { name: "Ironbound Mace", slot: "weapon", attackBonus: 2, defenseBonus: 1, hpBonus: 0, rarity: "common", description: "Simple iron bands around a oak shaft. Effective.", levelReq: 1 },
    { name: "Harbor Charm", slot: "accessory", attackBonus: 0, defenseBonus: 1, hpBonus: 2, rarity: "common", description: "A sailor's knot tied around a sea-smoothed stone.", levelReq: 1 },
    { name: "Driftwood Shield", slot: "shield", attackBonus: 0, defenseBonus: 3, hpBonus: 0, rarity: "common", description: "Salt-cured and surprisingly resilient.", levelReq: 1 },
  ],
  2: [
    { name: "Mountain Hide Jacket", slot: "armor", attackBonus: 0, defenseBonus: 4, hpBonus: 5, rarity: "uncommon", description: "Tanned from something that lived above the snowline.", levelReq: 2 },
    { name: "Forged Iron Blade", slot: "weapon", attackBonus: 4, defenseBonus: 0, hpBonus: 0, rarity: "uncommon", description: "Ironforge-quality steel. The balance is perfect.", levelReq: 2 },
    { name: "Stonecleaver Axe", slot: "weapon", attackBonus: 5, defenseBonus: 0, hpBonus: 0, rarity: "uncommon", description: "Dwarf-forged. Splits stone like kindling.", levelReq: 2 },
    { name: "Pilgrim's Greatsword", slot: "weapon", attackBonus: 6, defenseBonus: 0, hpBonus: 0, rarity: "uncommon", description: "Carried across three mountain ranges. The pilgrimage taught it patience.", levelReq: 2 },
    { name: "Roothollow Crystal", slot: "accessory", attackBonus: 1, defenseBonus: 1, hpBonus: 5, rarity: "uncommon", description: "Hums with a frequency just below hearing.", levelReq: 2 },
    { name: "Petrified Bark Shield", slot: "shield", attackBonus: 0, defenseBonus: 5, hpBonus: 2, rarity: "uncommon", description: "Ancient wood turned to stone. Heavier than it looks.", levelReq: 2 },
  ],
  3: [
    { name: "Dragonscale Cuirass", slot: "armor", attackBonus: 2, defenseBonus: 7, hpBonus: 10, rarity: "rare", description: "Each scale shimmers like a dying ember. Fire cannot touch you.", levelReq: 3 },
    { name: "Stormfang Blade", slot: "weapon", attackBonus: 8, defenseBonus: 0, hpBonus: 0, rarity: "rare", description: "Lightning arcs along the edge. The air tastes of ozone.", levelReq: 3 },
    { name: "Phoenix Feather Pendant", slot: "accessory", attackBonus: 2, defenseBonus: 2, hpBonus: 8, rarity: "rare", description: "Warm to the touch. Always warm. Even at night.", levelReq: 3 },
    { name: "Crystal Aegis", slot: "shield", attackBonus: 0, defenseBonus: 8, hpBonus: 5, rarity: "rare", description: "Refracts light into prismatic arcs. Impossibly light.", levelReq: 3 },
    { name: "Griffin Talon Gauntlet", slot: "weapon", attackBonus: 6, defenseBonus: 2, hpBonus: 3, rarity: "rare", description: "Curved claws of obsidian set in silver. Each strike tears deep.", levelReq: 3 },
    { name: "Abyssal Claymore", slot: "weapon", attackBonus: 10, defenseBonus: 0, hpBonus: 0, rarity: "rare", description: "The blade hums with a frequency that makes teeth ache. Something watches from inside the steel.", levelReq: 3 },
    { name: "Cinderheart Maul", slot: "weapon", attackBonus: 7, defenseBonus: 0, hpBonus: 5, rarity: "rare", description: "Still warm from the forge. Always warm. The fire never left.", levelReq: 3 },
    { name: "Stormwatch Cloak", slot: "armor", attackBonus: 0, defenseBonus: 5, hpBonus: 8, rarity: "legendary", description: "Woven from thundercloud threads. Lightning flows around you.", levelReq: 3 },
  ],
};

function rollLootDrop(speciesId: string, dangerLevel: number): InventoryItem | null {
  // Drop chance: 30% for danger 1, 45% for danger 2, 60% for danger 3
  const dropChance = 0.15 + dangerLevel * 0.15;
  if (Math.random() > dropChance) return null;

  // Fantasy creatures have higher drop tier
  const effectiveDanger = FANTASY_SPECIES.has(speciesId)
    ? Math.min(3, dangerLevel + 1)
    : dangerLevel;

  const pool = LOOT_TABLE[effectiveDanger] ?? LOOT_TABLE[1]!;
  const entry = pool[Math.floor(Math.random() * pool.length)];

  const id = `loot_${Date.now()}_${_lootCounter++}`;
  return {
    id,
    type: "equipment",
    name: entry.name,
    quantity: 1,
    rarity: entry.rarity,
    acquiredAt: Date.now(),
    equipmentData: {
      slot: entry.slot,
      attackBonus: entry.attackBonus,
      defenseBonus: entry.defenseBonus,
      hpBonus: entry.hpBonus,
      description: entry.description,
      levelReq: entry.levelReq,
      weaponVisual: entry.slot === "weapon" ? WEAPON_VISUALS[entry.name] : undefined,
      shieldVisual: entry.slot === "shield" ? SHIELD_VISUALS[entry.name] : undefined,
    },
  };
}
